import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Card from '../common/Card';
import moment from 'moment';
import {
  Restaurant,
  CalendarToday,
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

const Container = styled(Card)`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
  flex-wrap: wrap;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 5px rgba(25, 184, 136, 0.3);
  `}
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} ${props => getTheme(props.theme?.isDark).spacing.md};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.status === 'active') return getTheme(props.theme?.isDark).colors.success || '#19B888';
    if (props.status === 'completed') return getTheme(props.theme?.isDark).colors.border;
    return getTheme(props.theme?.isDark).colors.error || '#F44336';
  }};
  color: white;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-weight: 500;
`;

const PreferencesSection = styled.div`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0 0 ${props => getTheme(props.theme?.isDark).spacing.md} 0;
`;

const PreferencesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const PreferenceTag = styled.span`
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} ${props => getTheme(props.theme?.isDark).spacing.md};
  background: ${props => getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const DailyPlansSection = styled.div`
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const DailyPlanCard = styled.div`
  background: ${props => getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const DailyPlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const DailyPlanDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const MealsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const MealItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background: ${props => getTheme(props.theme?.isDark).colors.background};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const MealItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  cursor: pointer;
  user-select: none;
  
  &:hover {
    opacity: 0.8;
  }
`;

const MealDetails = styled.div`
  padding-top: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border-top: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  display: flex;
  flex-direction: row;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MealImage = styled.img`
  width: 200px;
  min-width: 200px;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    min-width: auto;
  }
`;

const MealDetailsContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  min-width: 0;
`;

const MealDetailsColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MealDetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  min-width: 0;
`;

const MealDetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const MealDetailLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const MealDetailContent = styled.div`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  line-height: 1.6;
`;

const IngredientsList = styled.ul`
  margin: 0;
  padding-left: ${props => getTheme(props.theme?.isDark).spacing.md};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const IngredientItem = styled.li`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
  font-size: 14px;
`;

const PreparationList = styled.ol`
  margin: 0;
  padding-left: ${props => getTheme(props.theme?.isDark).spacing.md};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const PreparationItem = styled.li`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.sm};
  font-size: 14px;
  line-height: 1.6;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  margin-left: auto;
  
  &:hover {
    opacity: 0.8;
  }
`;

const MealType = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  text-transform: capitalize;
  min-width: 80px;
`;

const MealName = styled.span`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  flex: 1;
`;

const NutritionInfo = styled.div`
  font-size: 11px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  font-style: italic;
`;

const ShowMoreButton = styled.button`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm} ${props => getTheme(props.theme?.isDark).spacing.md};
  background: ${props => getTheme(props.theme?.isDark).colors.primary};
  color: white;
  border: none;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.md};
  
  &:hover {
    opacity: 0.9;
  }
`;

const CustomerMealPlan = ({ mealPlan, theme: themeProp }) => {
  const { isDark } = useTheme();
  const theme = themeProp || { isDark };
  const [showAllDays, setShowAllDays] = useState(false);
  const [expandedMeals, setExpandedMeals] = useState({});
  const maxDaysToShow = 7;

  const toggleMealExpansion = (dailyPlanIndex, mealIndex) => {
    const key = `${dailyPlanIndex}-${mealIndex}`;
    setExpandedMeals(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!mealPlan) {
    return (
      <Container theme={theme}>
        <EmptyState theme={theme}>
          Pəhriz planı yoxdur
        </EmptyState>
      </Container>
    );
  }

  const mealTypeLabels = {
    breakfast: '🌅 Səhər yeməyi',
    lunch: '🍽️ Günorta yeməyi',
    dinner: '🍴 Axşam yeməyi',
    snack: '🍎 Qəlyanaltı',
  };

  const displayedDailyPlans = showAllDays 
    ? mealPlan.dailyPlans 
    : mealPlan.dailyPlans.slice(0, maxDaysToShow);

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>
          <Restaurant />
          Pəhriz Planı
        </Title>
        <StatusBadge theme={theme} status={mealPlan.status}>
          {mealPlan.status === 'active' ? <CheckCircle style={{ fontSize: 14 }} /> : <Cancel style={{ fontSize: 14 }} />}
          {mealPlan.status === 'active' ? 'Aktiv' : mealPlan.status === 'completed' ? 'Tamamlanıb' : 'Dayandırılıb'}
        </StatusBadge>
      </Header>

      <InfoGrid theme={theme}>
        <InfoItem theme={theme}>
          <InfoLabel theme={theme}>Başlanğıc Tarixi</InfoLabel>
          <InfoValue theme={theme}>
            {moment(mealPlan.startDate).format('DD.MM.YYYY')}
          </InfoValue>
        </InfoItem>
        <InfoItem theme={theme}>
          <InfoLabel theme={theme}>Bitiş Tarixi</InfoLabel>
          <InfoValue theme={theme}>
            {moment(mealPlan.endDate).format('DD.MM.YYYY')}
          </InfoValue>
        </InfoItem>
        <InfoItem theme={theme}>
          <InfoLabel theme={theme}>Günlər</InfoLabel>
          <InfoValue theme={theme}>
            {mealPlan.dailyPlans?.length || 0} gün
          </InfoValue>
        </InfoItem>
        <InfoItem theme={theme}>
          <InfoLabel theme={theme}>Status</InfoLabel>
          <InfoValue theme={theme}>
            {mealPlan.generationStatus === 'completed' ? '✅ Hazırdır' : 
             mealPlan.generationStatus === 'processing' ? '⏳ Hazırlanır' :
             mealPlan.generationStatus === 'pending' ? '⏸️ Gözləyir' : '❌ Xəta'}
          </InfoValue>
        </InfoItem>
      </InfoGrid>

      {mealPlan.preferences && (
        <PreferencesSection theme={theme}>
          <SectionTitle theme={theme}>Pəhriz Üstünlükləri</SectionTitle>
          <PreferencesList theme={theme}>
            {mealPlan.preferences.dietType && (
              <PreferenceTag theme={theme}>
                Pəhriz növü: {mealPlan.preferences.dietType}
              </PreferenceTag>
            )}
            {mealPlan.preferences.likedIngredients && mealPlan.preferences.likedIngredients.length > 0 && (
              <PreferenceTag theme={theme}>
                Bəyənilən: {mealPlan.preferences.likedIngredients.join(', ')}
              </PreferenceTag>
            )}
            {mealPlan.preferences.dislikedIngredients && mealPlan.preferences.dislikedIngredients.length > 0 && (
              <PreferenceTag theme={theme}>
                Bəyənilməyən: {mealPlan.preferences.dislikedIngredients.join(', ')}
              </PreferenceTag>
            )}
            {mealPlan.preferences.allergies && mealPlan.preferences.allergies.length > 0 && (
              <PreferenceTag theme={theme}>
                Allergiyalar: {mealPlan.preferences.allergies.join(', ')}
              </PreferenceTag>
            )}
          </PreferencesList>
        </PreferencesSection>
      )}

      {mealPlan.dailyPlans && mealPlan.dailyPlans.length > 0 && (
        <DailyPlansSection theme={theme}>
          <SectionTitle theme={theme}>Günlük Planlar</SectionTitle>
          {displayedDailyPlans.map((dailyPlan, index) => (
            <DailyPlanCard key={index} theme={theme}>
              <DailyPlanHeader theme={theme}>
                <DailyPlanDate theme={theme}>
                  <CalendarToday style={{ fontSize: 16, marginRight: 8, verticalAlign: 'middle' }} />
                  Gün {dailyPlan.day} - {moment(dailyPlan.date).format('DD.MM.YYYY')}
                </DailyPlanDate>
                {dailyPlan.totalNutrition && (
                  <NutritionInfo theme={theme}>
                    {dailyPlan.totalNutrition.calories && (
                      <span>🔥 {Math.round(dailyPlan.totalNutrition.calories)} kcal</span>
                    )}
                    {dailyPlan.totalNutrition.proteins && (
                      <span>🥩 {Math.round(dailyPlan.totalNutrition.proteins)}g protein</span>
                    )}
                    {dailyPlan.totalNutrition.carbs && (
                      <span>🍞 {Math.round(dailyPlan.totalNutrition.carbs)}g karbohidrat</span>
                    )}
                    {dailyPlan.totalNutrition.fats && (
                      <span>🧈 {Math.round(dailyPlan.totalNutrition.fats)}g yağ</span>
                    )}
                  </NutritionInfo>
                )}
              </DailyPlanHeader>
              {dailyPlan.meals && dailyPlan.meals.length > 0 && (
                <MealsList theme={theme}>
                  {dailyPlan.meals.map((meal, mealIndex) => {
                    const mealKey = `${index}-${mealIndex}`;
                    const isExpanded = expandedMeals[mealKey];
                    
                    return (
                      <MealItem key={mealIndex} theme={theme}>
                        <MealItemHeader 
                          theme={theme}
                          onClick={() => toggleMealExpansion(index, mealIndex)}
                        >
                          <MealType theme={theme}>
                            {mealTypeLabels[meal.mealType] || meal.mealType}
                          </MealType>
                          <MealName theme={theme}>{meal.mealName}</MealName>
                          {meal.nutrition && (
                            <NutritionInfo theme={theme}>
                              {meal.nutrition.calories && (
                                <span>{Math.round(meal.nutrition.calories)} kcal</span>
                              )}
                            </NutritionInfo>
                          )}
                          <ExpandButton theme={theme}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </ExpandButton>
                        </MealItemHeader>
                        
                        {isExpanded && (
                          <MealDetails theme={theme}>
                            {meal.imageUrl && (
                              <MealImage 
                                src={meal.imageUrl} 
                                alt={meal.mealName}
                                theme={theme}
                              />
                            )}
                            
                            <MealDetailsContent theme={theme}>
                              <MealDetailsColumns theme={theme}>
                                <MealDetailsColumn theme={theme}>
                                  {meal.ingredients && meal.ingredients.length > 0 && (
                                    <MealDetailSection theme={theme}>
                                      <MealDetailLabel theme={theme}>İngredientlər</MealDetailLabel>
                                      <IngredientsList>
                                        {meal.ingredients.map((ingredient, ingIndex) => (
                                          <IngredientItem key={ingIndex} theme={theme}>
                                            {ingredient}
                                          </IngredientItem>
                                        ))}
                                      </IngredientsList>
                                    </MealDetailSection>
                                  )}
                                  
                                  {meal.servingSize && (
                                    <MealDetailSection theme={theme}>
                                      <MealDetailLabel theme={theme}>Porsiya Ölçüsü</MealDetailLabel>
                                      <MealDetailContent theme={theme}>
                                        {meal.servingSize}
                                      </MealDetailContent>
                                    </MealDetailSection>
                                  )}
                                </MealDetailsColumn>
                                
                                <MealDetailsColumn theme={theme}>
                                  {meal.preparation && (
                                    <MealDetailSection theme={theme}>
                                      <MealDetailLabel theme={theme}>Hazırlanma Üsulu</MealDetailLabel>
                                      <MealDetailContent theme={theme}>
                                        {(() => {
                                          // Hazırlanma üsulunu parse et - array və ya string ola bilər
                                          let preparationSteps = [];
                                          
                                          if (Array.isArray(meal.preparation)) {
                                            preparationSteps = meal.preparation;
                                          } else if (typeof meal.preparation === 'string') {
                                            // String-i addımlara böl - nömrələrə görə və ya yeni sətirlərə görə
                                            const text = meal.preparation.trim();
                                            
                                            // Əgər nömrələnmiş format varsa (1. 2. 3. və ya 1) 2) 3))
                                            const numberedMatch = text.match(/^\d+[.)]\s*/);
                                            if (numberedMatch) {
                                              preparationSteps = text
                                                .split(/\d+[.)]\s*/)
                                                .filter(step => step.trim().length > 0)
                                                .map(step => step.trim());
                                            } else {
                                              // Yeni sətirlərə görə böl
                                              preparationSteps = text
                                                .split(/\n+/)
                                                .filter(step => step.trim().length > 0)
                                                .map(step => step.trim());
                                            }
                                          }
                                          
                                          if (preparationSteps.length === 0) {
                                            return <div>{meal.preparation}</div>;
                                          }
                                          
                                          return (
                                            <PreparationList theme={theme}>
                                              {preparationSteps.map((step, stepIndex) => (
                                                <PreparationItem key={stepIndex} theme={theme}>
                                                  {step}
                                                </PreparationItem>
                                              ))}
                                            </PreparationList>
                                          );
                                        })()}
                                      </MealDetailContent>
                                    </MealDetailSection>
                                  )}
                                </MealDetailsColumn>
                                
                                <MealDetailsColumn theme={theme}>
                                  {meal.nutrition && (
                                    <MealDetailSection theme={theme}>
                                      <MealDetailLabel theme={theme}>Qida Dəyərləri</MealDetailLabel>
                                      <MealDetailContent theme={theme}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {meal.nutrition.calories && (
                                            <div>🔥 Kalori: {Math.round(meal.nutrition.calories)} kcal</div>
                                          )}
                                          {meal.nutrition.proteins && (
                                            <div>🥩 Protein: {Math.round(meal.nutrition.proteins)}g</div>
                                          )}
                                          {meal.nutrition.carbs && (
                                            <div>🍞 Karbohidrat: {Math.round(meal.nutrition.carbs)}g</div>
                                          )}
                                          {meal.nutrition.fats && (
                                            <div>🧈 Yağ: {Math.round(meal.nutrition.fats)}g</div>
                                          )}
                                        </div>
                                      </MealDetailContent>
                                    </MealDetailSection>
                                  )}
                                </MealDetailsColumn>
                              </MealDetailsColumns>
                            </MealDetailsContent>
                          </MealDetails>
                        )}
                      </MealItem>
                    );
                  })}
                </MealsList>
              )}
            </DailyPlanCard>
          ))}
          {mealPlan.dailyPlans.length > maxDaysToShow && (
            <ShowMoreButton 
              theme={theme} 
              onClick={() => setShowAllDays(!showAllDays)}
            >
              {showAllDays ? 'Daha az göstər' : `Daha çox göstər (+${mealPlan.dailyPlans.length - maxDaysToShow} gün)`}
            </ShowMoreButton>
          )}
        </DailyPlansSection>
      )}
    </Container>
  );
};

export default CustomerMealPlan;

